import { useEffect, useState } from "react"
import {
  OLevelResult,
  stageLevel,
  useAppNotificationStore,
  useApplicationStore,
  useForm,
  useProfileStore,
} from "stores"
import {
  AnchorButton,
  Button,
  H4,
  H6,
  P1,
  P2,
  Table,
  TableData,
  TableHeader,
  TextButton,
} from "components"
import { Eye, EyeSlash } from "iconsax-react"
import { formatDate } from "utils"
import { useNavigate } from "react-router-dom"

export const Complete: React.FC<{}> = () => {
  const { setDashboardContext, toast } = useAppNotificationStore()
  const { submitting, isSubmitting } = useForm()
  const { updateStage } = useApplicationStore()
  const navigate = useNavigate()
  const {
    getEducationalRecords,
    getOLevelResults,
    getEmploymentRecords,
    educationalRecords,
    oLevelResults,
    employmentRecords,
  } = useApplicationStore()
  const { profile } = useProfileStore()
  const [viewResults, setViewResults] = useState<OLevelResult>()

  useEffect(() => {
    setDashboardContext("Preview Profile")
  }, [setDashboardContext])

  useEffect(() => {
    getEducationalRecords()
    getEmploymentRecords()
    getOLevelResults()
  }, [getEducationalRecords, getEmploymentRecords, getOLevelResults])

  const handleSubmit = async () => {
    try {
      submitting(true)
      await updateStage({ currentStage: "ApplicationSumitted" }).then(
        (resp) => {
          submitting(false)
          if (resp.isSuccessful === true) {
            toast.success(resp.message)
            navigate("/dashboard/admission-status")
          } else {
            toast.error(resp.message)
          }
        }
      )
    } catch (error: any) {
      submitting(false)
      console.log(error)
      toast.error(error?.message as string)
    }
  }

  return (
    <div className="flex flex-col gap-12 items-center">
      <div className="flex flex-col items-center gap-6">
        <H4 className="font-semibold text-accentText">Preview Profile</H4>
        <P1 className="text-accentGrey1 text-center">
          This is your profile details. Kindly check them over and make sure
          they're correct before submitting.
        </P1>
      </div>

      <div
        className={`w-[200px] h-[200px] rounded-xl border border-accentGrey1 ${
          profile?.avatar === "" ? "border" : ""
        }`}
      >
        <img
          src={profile?.avatar}
          alt=""
          className="w-[200px] h-[200px] rounded-xl border border-accentGrey1 object-cover"
        />
      </div>

      <div className="w-full flex flex-col gap-8">
        <div className="w-full flex flex-col gap-4">
          <div className="w-full p-4 bg-pryExtraLight">
            <H6 className="text-pryBase font-semibold">Bio-Data</H6>
          </div>
        </div>

        <div className="w-full flex flex-col lg:flex-row gap-8 items-start">
          <div className="space-y-[5px] w-full">
            <P2 className="text-accentGrey1 font-medium">Full Name</P2>
            <div className="w-full border-b-2 border-accentGrey3 bg-white p-3 text-accentText font-medium">
              {profile?.firstname} {profile?.surname} {profile?.othername}
            </div>
          </div>
        </div>

        <div className="w-full flex flex-col lg:flex-row gap-8 items-start">
          <div className="space-y-[5px] w-full">
            <P2 className="text-accentGrey1 font-medium">Email address</P2>
            <div className="w-full border-b-2 line-clamp-1 border-accentGrey3 bg-white p-3 text-accentText font-medium">
              {profile?.email}
            </div>
          </div>
          <div className="space-y-[5px] w-full">
            <P2 className="text-accentGrey1 font-medium">Phone Number</P2>
            <div className="w-full border-b-2 border-accentGrey3 bg-white p-3 text-accentText font-medium">
              {profile?.mobileNo}
            </div>
          </div>
        </div>

        <div className="w-full flex flex-col lg:flex-row gap-8 items-start">
          <div className="space-y-[5px] w-full">
            <P2 className="text-accentGrey1 font-medium">Marital Status</P2>
            <div className="w-full border-b-2 border-accentGrey3 bg-white p-3 text-accentText font-medium">
              {profile?.maritalStatus}
            </div>
          </div>
          <div className="space-y-[5px] w-full">
            <P2 className="text-accentGrey1 font-medium">Date of Birth</P2>
            <div className="w-full border-b-2 border-accentGrey3 bg-white p-3 text-accentText font-medium">
              {formatDate(profile?.dob!)}
            </div>
          </div>
        </div>

        <div className="space-y-[5px] w-full">
          <P2 className="text-accentGrey1 font-medium">Address</P2>
          <div className="w-full border-b-2 border-accentGrey3 bg-white p-3 text-accentText font-medium">
            {profile?.address}
          </div>
        </div>

        <div className="w-full flex flex-col lg:flex-row gap-8 items-start">
          <div className="space-y-[5px] w-full">
            <P2 className="text-accentGrey1 font-medium">State of Origin</P2>
            <div className="w-full border-b-2 border-accentGrey3 bg-white p-3 text-accentText font-medium">
              {profile?.state}
            </div>
          </div>
          <div className="space-y-[5px] w-full">
            <P2 className="text-accentGrey1 font-medium">
              Local Government Area
            </P2>
            <div className="w-full border-b-2 border-accentGrey3 bg-white p-3 text-accentText font-medium">
              {profile?.lga}
            </div>
          </div>
        </div>

        <div className="w-full p-4 bg-pryExtraLight">
          <H6 className="text-pryBase font-semibold">Next of Kin Details</H6>
        </div>

        <div className="w-full flex flex-col lg:flex-row gap-8 items-start">
          <div className="space-y-[5px] w-full">
            <P2 className="text-accentGrey1 font-medium">Full Name</P2>
            <div className="w-full border-b-2 border-accentGrey3 bg-white p-3 text-accentText font-medium">
              {profile?.nextName}
            </div>
          </div>
        </div>

        <div className="w-full flex flex-col lg:flex-row gap-8 items-start">
          <div className="space-y-[5px] w-full">
            <P2 className="text-accentGrey1 font-medium">Email address</P2>
            <div className="w-full border-b-2 border-accentGrey3 bg-white p-3 text-accentText font-medium">
              {profile?.nextEmail ?? "--"}
            </div>
          </div>
          <div className="space-y-[5px] w-full">
            <P2 className="text-accentGrey1 font-medium">Phone Number</P2>
            <div className="w-full border-b-2 border-accentGrey3 bg-white p-3 text-accentText font-medium">
              {profile?.nextPhoneNumber}
            </div>
          </div>
        </div>

        <div className="w-full flex flex-col lg:flex-row gap-8 items-start">
          <div className="space-y-[5px] w-full">
            <P2 className="text-accentGrey1 font-medium">Gender</P2>
            <div className="w-full border-b-2 border-accentGrey3 bg-white p-3 text-accentText font-medium">
              {profile?.nextGender}
            </div>
          </div>
          <div className="space-y-[5px] w-full">
            <P2 className="text-accentGrey1 font-medium">Relationship</P2>
            <div className="w-full border-b-2 border-accentGrey3 bg-white p-3 text-accentText font-medium">
              {profile?.relationship}
            </div>
          </div>
        </div>

        <div className="space-y-[5px] w-full">
          <P2 className="text-accentGrey1 font-medium">Address</P2>
          <div className="w-full border-b-2 border-accentGrey3 bg-white p-3 text-accentText font-medium">
            {profile?.nextAddress}
          </div>
        </div>

        <div className="w-full p-4 bg-pryExtraLight">
          <H6 className="text-pryBase font-semibold">Program Choices</H6>
        </div>

        <div className="w-full flex flex-col lg:flex-row gap-8 items-start">
          <div className="space-y-[5px] w-full">
            <P2 className="text-accentGrey1 font-medium">Selected Program</P2>
            <div className="w-full border-b-2 border-accentGrey3 bg-white p-3 text-accentText font-medium">
              {profile?.department}
            </div>
          </div>
          <div className="space-y-[5px] w-full">
            <P2 className="text-accentGrey1 font-medium">Program Type</P2>
            <div className="w-full border-b-2 border-accentGrey3 bg-white p-3 text-accentText font-medium">
              {profile?.programmeLevel}
            </div>
          </div>
        </div>

        <div className="w-full flex flex-col lg:flex-row gap-8 items-start">
          <div className="space-y-[5px] w-full">
            <P2 className="text-accentGrey1 font-medium">Application Type</P2>
            <div className="w-full border-b-2 border-accentGrey3 bg-white p-3 text-accentText font-medium">
              Online
            </div>
          </div>
          <div className="space-y-[5px] w-full" />
        </div>

        <div className="w-full p-4 bg-pryExtraLight">
          <H6 className="text-pryBase font-semibold">School Records</H6>
        </div>

        <div className="w-full">
          <Table
            cols={5}
            headers={[
              <TableHeader>#</TableHeader>,
              <TableHeader>Qualification</TableHeader>,
              <TableHeader>Start</TableHeader>,
              <TableHeader>End</TableHeader>,
            ]}
            rows={educationalRecords.map((record, index) => ({
              data: [
                <TableData key={index}>{index + 1}</TableData>,
                <TableData key={index}>{record.qualification}</TableData>,
                <TableData key={index}>{record.startDate}</TableData>,
                <TableData key={index}>{record.endDate}</TableData>,
              ],
            }))}
          />
        </div>

        <div className="w-full p-4 bg-pryExtraLight">
          <H6 className="text-pryBase font-semibold">Employment Records</H6>
        </div>

        <div className="w-full">
          <Table
            cols={4}
            headers={[
              <TableHeader>#</TableHeader>,
              <TableHeader>Employer's&nbsp;Name</TableHeader>,
              <TableHeader>From</TableHeader>,
              <TableHeader>To</TableHeader>,
            ]}
            rows={employmentRecords.map((record, index) => ({
              data: [
                <TableData key={index}>{index + 1}</TableData>,
                <TableData key={index}>{record.employerName}</TableData>,
                <TableData key={index}>{record.startDate}</TableData>,
                <TableData key={index}>{record.endDate}</TableData>,
              ],
            }))}
          />
        </div>

        <div className="w-full p-4 bg-pryExtraLight">
          <H6 className="text-pryBase font-semibold">Examination Records</H6>
        </div>

        <div className="w-full">
          <Table
            cols={5}
            headers={[
              <TableHeader>#</TableHeader>,
              <TableHeader>Exam&nbsp;Type</TableHeader>,
              <TableHeader>Exam&nbsp;Number</TableHeader>,
              <TableHeader>Year</TableHeader>,
              <TableHeader>Action</TableHeader>,
            ]}
            rows={oLevelResults.map((record, index) => ({
              data: [
                <TableData key={index}>{index + 1}</TableData>,
                <TableData key={index}>{record.examName}</TableData>,
                <TableData key={index}>{record.examNumber}</TableData>,
                <TableData key={index}>{record.examYear}</TableData>,
                <TextButton
                  type="button"
                  title="View"
                  className="flex gap-3 items-center"
                  onClick={() => {
                    setViewResults(record)
                  }}
                >
                  <Eye
                    className="text-accentText"
                    size={18}
                  />
                  <P1 className="hidden lg:block text-accentGrey1 font-medium">
                    View
                  </P1>
                </TextButton>,
              ],
            }))}
          />
          <br />
          {viewResults !== undefined && (
            <div className="w-full">
              <div className="flex justify-end">
                <TextButton
                  title="Hide result"
                  className="text-sm h-full gap-1 flex item-center"
                  onClick={() => setViewResults(undefined)}
                >
                  <EyeSlash className="w-4" />
                  <span>Hide</span>
                </TextButton>
              </div>
              <Table
                cols={4}
                headers={[
                  <TableHeader>#</TableHeader>,
                  <TableHeader>Subject</TableHeader>,
                  <TableHeader>Grade</TableHeader>,
                ]}
                rows={
                  viewResults?.subjects?.map((sub, index) => ({
                    data: [
                      <TableData key={index}>{index + 1}</TableData>,
                      <TableData key={index}>{sub.subjectName}</TableData>,
                      <TableData key={index}>{sub.grade}</TableData>,
                    ],
                  }))!
                }
              />
            </div>
          )}
        </div>

        <div className="flex justify-end items-center mt-2">
          <Button
            type="button"
            title="Print"
            color="primary"
            submitting={isSubmitting ? "true" : "false"}
            disabled={
              isSubmitting ||
              stageLevel[profile?.nextStage!] < stageLevel.SubmitApplication
            }
            className="w-full lg:w-max"
            onClick={() => handleSubmit()}
          >
            Submit
          </Button>
        </div>
      </div>
    </div>
  )
}
